import { wallStatMultiplier, type StageDef } from '../data/stages.ts';
import { TICK_SECONDS, type Rule, type TargetKind } from '../model/types.ts';
import type { ResolvedHero, RuntimeAbility } from './build.ts';
import { normalizeSeed, rngNext } from './rng.ts';

interface Combatant {
  id: string;
  name: string;
  hp: number;
  maxHp: number;
  attack: number;
  armor: number;
  shield: number;
  role: 'hero' | 'trash' | 'elite' | 'boss';
  attackEvery: number;
  attackTimer: number;
  stacks: number;
  stackGrowth: number;
  stackEvery: number;
  stackDr: number;
  stackDrCap: number;
  /** Stripped to zero stacks, and not rebuilt yet. Still counts as armor for the break bonus and for armor-break rules. */
  armorBroken: boolean;
  alive: boolean;
}

export interface CombatState {
  stageId: number;
  stageName: string;
  zoneName: string;
  isWall: boolean;
  wallHint?: string;
  threat: string[];
  limitTicks: number;
  hero: Combatant;
  enemies: Combatant[];
  waveIndex: number;
  waveCount: number;
  tick: number;
  rngState: number;
  cooldowns: Record<string, number>;
  basicCooldown: number;
  abilities: RuntimeAbility[];
  rules: Rule[];
  armorBreak: number;
  healPower: number;
  bonusStrip: number;
  groveHeal: number;
  /** Blocked-damage healing waiting to be written as one line for this volley. */
  grovePending: number;
  debtOnHeal: boolean;
  resourcePool: number;
  debt: number;
  log: string[];
  recordLog: boolean;
  kills: number;
  damageDealt: number;
  goldPerKill: number;
  clearGold: number;
  clearXp: number;
  clearDust: number;
  dropRolls: number;
  finished: boolean;
  outcome: 'victory' | 'defeat' | null;
  reason: 'cleared' | 'dead' | 'timeout' | null;
  lastHits: { text: string; who: string }[];
  waves: StageDef['waves'];
}

export interface CombatResult {
  outcome: 'victory' | 'defeat';
  reason: 'cleared' | 'dead' | 'timeout';
  ticks: number;
  seconds: number;
  kills: number;
  gold: number;
  xp: number;
  dust: number;
  dropRolls: number;
  damageDealt: number;
  log: string[];
  heroHp: number;
  heroMaxHp: number;
  stageId: number;
}

const BASIC_COOLDOWN = 4;
const BASIC_COEFF = 1;

function spawnWave(state: CombatState, index: number): void {
  const wave = state.waves[index];
  if (!wave) return;
  state.enemies = wave.enemies.map((enemy, i) => ({
    id: `w${index}-${i}`,
    name: enemy.name,
    hp: enemy.hp,
    maxHp: enemy.hp,
    attack: enemy.attack,
    armor: enemy.armor,
    shield: 0,
    role: enemy.role,
    attackEvery: enemy.attackEveryTicks,
    attackTimer: enemy.attackEveryTicks,
    stacks: enemy.armorStacks ?? 0,
    stackGrowth: enemy.armorStackGrowth ?? 0,
    stackEvery: enemy.armorStackEveryTicks ?? 0,
    stackDr: enemy.stackDr ?? 0,
    stackDrCap: enemy.stackDrCap ?? 0,
    armorBroken: false,
    alive: true,
  }));
  state.waveIndex = index;
}

export function createCombat(input: {
  hero: ResolvedHero;
  stage: StageDef;
  seed: number;
  recordLog?: boolean;
  /** Walls scale up below their level floor. Set false only to measure the unscaled wall. */
  scaleWalls?: boolean;
}): CombatState {
  const scale =
    input.stage.isWall && input.scaleWalls !== false ? wallStatMultiplier(input.stage.id, input.hero.level) : 1;
  const waves =
    scale === 1
      ? input.stage.waves
      : input.stage.waves.map((wave) => ({
          enemies: wave.enemies.map((enemy) => ({
            ...enemy,
            hp: Math.max(1, Math.round(enemy.hp * scale)),
            attack: Math.max(1, Math.round(enemy.attack * scale)),
          })),
        }));
  const state: CombatState = {
    stageId: input.stage.id,
    stageName: input.stage.name,
    zoneName: input.stage.zoneName,
    isWall: input.stage.isWall,
    wallHint: input.stage.wallHint,
    threat: input.stage.threat,
    limitTicks: input.stage.limitTicks,
    hero: {
      id: 'hero',
      name: 'Ash',
      hp: input.hero.maxHp,
      maxHp: input.hero.maxHp,
      attack: input.hero.attack,
      armor: input.hero.armor,
      shield: 0,
      role: 'hero',
      attackEvery: 0,
      attackTimer: 0,
      stacks: 0,
      stackGrowth: 0,
      stackEvery: 0,
      stackDr: 0,
      stackDrCap: 0,
      armorBroken: false,
      alive: true,
    },
    enemies: [],
    waveIndex: 0,
    waveCount: input.stage.waves.length,
    tick: 0,
    rngState: normalizeSeed(input.seed),
    cooldowns: {},
    basicCooldown: 0,
    abilities: input.hero.abilities,
    rules: input.hero.rules,
    armorBreak: input.hero.armorBreak,
    healPower: input.hero.healPower,
    bonusStrip: input.hero.bonusStrip,
    groveHeal: input.hero.groveHeal,
    grovePending: 0,
    debtOnHeal: input.hero.debtOnHeal,
    resourcePool: input.hero.resourcePool,
    debt: 0,
    log: [],
    recordLog: input.recordLog !== false,
    kills: 0,
    damageDealt: 0,
    goldPerKill: input.stage.goldPerKill,
    clearGold: input.stage.clearGold,
    clearXp: input.stage.clearXp,
    clearDust: input.stage.clearDust,
    dropRolls: input.stage.dropRolls,
    finished: false,
    outcome: null,
    reason: null,
    lastHits: [],
    waves,
  };
  for (const ability of input.hero.abilities) state.cooldowns[ability.id] = 0;
  spawnWave(state, 0);
  return state;
}

function note(state: CombatState, line: string): void {
  if (!state.recordLog) return;
  state.log.push(`${(state.tick * TICK_SECONDS).toFixed(1)}s ${line}`);
  if (state.log.length > 40) state.log.shift();
}

function roll(state: CombatState): number {
  const next = rngNext(state.rngState);
  state.rngState = next.state;
  return next.value;
}

function variance(state: CombatState): number {
  return 0.95 + roll(state) * 0.1;
}

function reduction(target: Combatant): number {
  if (target.stackDr > 0) return Math.min(target.stackDrCap, target.stacks * target.stackDr);
  return target.armor / (target.armor + 100);
}

/** Armor still counts after a full strip, until the foe rebuilds stacks. */
function foeHasArmor(foe: Combatant): boolean {
  return foe.stacks > 0 || foe.armor > 0 || foe.armorBroken;
}

function armorBreakBonus(state: CombatState, foe: Combatant): boolean {
  return state.armorBreak > 0 && (foe.stacks > 0 || foe.armorBroken);
}

function dealToEnemy(state: CombatState, target: Combatant, raw: number, ignoreDr: number): number {
  const dr = reduction(target);
  const ignore = Math.max(0, Math.min(1, ignoreDr));
  const varied = raw * variance(state);
  const dealt = Math.max(1, Math.round(varied * (1 - dr) * (1 - ignore) + varied * ignore));
  target.hp -= dealt;
  state.damageDealt += dealt;
  return dealt;
}

function flushGrove(state: CombatState): void {
  if (state.grovePending <= 0) return;
  note(state, `grove mends ${state.grovePending}`);
  state.grovePending = 0;
}

function dealToHero(state: CombatState, raw: number): number {
  const dr = state.hero.armor / (state.hero.armor + 100);
  const varied = raw * variance(state);
  let dmg = Math.max(1, Math.round(varied * (1 - dr)));
  let blocked = Math.max(0, Math.round(varied) - dmg);
  if (state.hero.shield > 0) {
    const absorbed = Math.min(state.hero.shield, dmg);
    state.hero.shield -= absorbed;
    dmg -= absorbed;
    blocked += absorbed;
  }
  state.hero.hp -= dmg;
  if (state.groveHeal > 0 && blocked > 0 && state.hero.hp > 0) {
    const grow = Math.min(state.resourcePool, Math.max(0, Math.round(blocked * state.groveHeal)));
    if (grow > 0) {
      state.hero.hp = Math.min(state.hero.maxHp, state.hero.hp + grow);
      state.grovePending += grow;
    }
  }
  return dmg;
}

function withDebt(state: CombatState, raw: number): number {
  if (!state.debtOnHeal || state.debt <= 0) return raw;
  const bonus = Math.round(state.debt * 0.5);
  state.debt = 0;
  return raw + bonus;
}

function living(enemies: Combatant[]): Combatant[] {
  return enemies.filter((e) => e.alive && e.hp > 0);
}

function resolveTarget(state: CombatState, kind: TargetKind): Combatant | null {
  const foes = living(state.enemies);
  if (kind === 'self') return state.hero;
  if (foes.length === 0) return null;
  if (kind === 'boss') return foes.find((e) => e.role === 'boss' || e.role === 'elite') ?? null;
  if (kind === 'highestHp') return [...foes].sort((a, b) => b.hp - a.hp)[0] ?? null;
  return [...foes].sort((a, b) => a.hp - b.hp)[0] ?? null;
}

function conditionMet(state: CombatState, rule: Rule, target: Combatant | null): boolean {
  const condition = rule.condition;
  if (condition.kind === 'always') return true;
  if (condition.kind === 'selfHpBelow') return state.hero.hp / state.hero.maxHp <= condition.pct / 100;
  if (condition.kind === 'enemyCountAtLeast') return living(state.enemies).length >= condition.count;
  if (!target || target.role === 'hero') return false;
  if (condition.kind === 'targetIsEliteOrBoss') return target.role === 'elite' || target.role === 'boss';
  return foeHasArmor(target);
}

function abilityReady(state: CombatState, ability: RuntimeAbility): boolean {
  return (state.cooldowns[ability.id] ?? 0) <= 0;
}

function chooseAbility(state: CombatState): { ability: RuntimeAbility; target: Combatant } | null {
  for (const rule of state.rules) {
    const ability = state.abilities.find((a) => a.id === rule.abilityId);
    if (!ability || !abilityReady(state, ability)) continue;
    const selfCast = ability.kind === 'heal' || ability.kind === 'shield';
    const target = resolveTarget(state, selfCast ? 'self' : rule.target);
    if (!target) continue;
    if (!conditionMet(state, rule, selfCast ? target : target)) continue;
    if (selfCast && target.id !== 'hero') continue;
    if (!selfCast && target.id === 'hero') continue;
    return { ability, target };
  }
  return null;
}

function finish(state: CombatState, outcome: 'victory' | 'defeat', reason: CombatResult['reason']): void {
  state.finished = true;
  state.outcome = outcome;
  state.reason = reason;
  state.hero.alive = state.hero.hp > 0;
  note(state, outcome === 'victory' ? 'stage cleared' : reason === 'timeout' ? 'enrage — the stage holds' : 'the braid snaps');
}

function cast(state: CombatState, ability: RuntimeAbility, target: Combatant): void {
  state.cooldowns[ability.id] = ability.cooldownTicks;
  if (ability.kind === 'heal') {
    const amount = Math.max(1, Math.round(state.healPower * ability.coeff * variance(state)));
    state.hero.hp = Math.min(state.hero.maxHp, state.hero.hp + amount);
    note(state, `${ability.name} mends ${amount}`);
    if (state.debtOnHeal) {
      state.debt = Math.min(state.resourcePool, state.debt + amount);
      note(state, `debt records ${amount}`);
    }
    state.lastHits.push({ text: `+${amount}`, who: 'hero' });
    return;
  }
  if (ability.kind === 'shield') {
    const amount = Math.max(1, Math.round(state.hero.maxHp * ability.coeff));
    state.hero.shield += amount;
    note(state, `${ability.name} guards ${amount}`);
    state.lastHits.push({ text: `guard ${amount}`, who: 'hero' });
    return;
  }

  const foes = ability.kind === 'aoe' ? living(state.enemies) : [target];
  for (const foe of foes) {
    let raw = withDebt(state, state.hero.attack * ability.coeff);
    if (armorBreakBonus(state, foe)) raw *= 1 + state.armorBreak / 100;
    const dealt = dealToEnemy(state, foe, raw, ability.ignoreDr);
    if (ability.strip > 0 && (foe.stacks > 0 || foe.stackDr > 0)) {
      const strip = ability.strip + state.bonusStrip;
      const before = foe.stacks;
      foe.stacks = Math.max(0, foe.stacks - strip);
      if (before > 0 && foe.stacks === 0) foe.armorBroken = true;
      note(state, `${ability.name} hits ${foe.name} for ${dealt} (armor ${before} → ${foe.stacks})`);
    } else {
      note(state, `${ability.name} hits ${foe.name} for ${dealt}`);
    }
    state.lastHits.push({ text: String(dealt), who: foe.id });
  }
}

export function stepCombat(state: CombatState): CombatState {
  if (state.finished) return state;
  state.tick += 1;
  state.lastHits = [];

  for (const enemy of living(state.enemies)) {
    if (enemy.stackEvery > 0 && state.tick % enemy.stackEvery === 0) {
      enemy.stacks += enemy.stackGrowth;
      enemy.armorBroken = false;
    }
    enemy.attackTimer -= 1;
    if (enemy.attackTimer <= 0) {
      const dmg = dealToHero(state, enemy.attack);
      enemy.attackTimer = enemy.attackEvery;
      if (dmg > 0) state.lastHits.push({ text: `-${dmg}`, who: 'hero' });
      if (state.hero.hp <= 0) {
        state.hero.hp = 0;
        flushGrove(state);
        finish(state, 'defeat', 'dead');
        return state;
      }
    }
  }
  flushGrove(state);

  for (const id of Object.keys(state.cooldowns)) {
    const current = state.cooldowns[id] ?? 0;
    if (current > 0) state.cooldowns[id] = current - 1;
  }
  if (state.basicCooldown > 0) state.basicCooldown -= 1;

  const chosen = chooseAbility(state);
  if (chosen) {
    cast(state, chosen.ability, chosen.target);
  } else if (state.basicCooldown <= 0) {
    const target = resolveTarget(state, 'lowestHp');
    if (target) {
      state.basicCooldown = BASIC_COOLDOWN;
      let raw = withDebt(state, state.hero.attack * BASIC_COEFF);
      if (armorBreakBonus(state, target)) raw *= 1 + state.armorBreak / 100;
      const dealt = dealToEnemy(state, target, raw, 0);
      state.lastHits.push({ text: String(dealt), who: target.id });
    }
  }

  for (const enemy of state.enemies) {
    if (enemy.alive && enemy.hp <= 0) {
      enemy.alive = false;
      enemy.hp = 0;
      state.kills += 1;
      note(state, `${enemy.name} falls`);
    }
  }

  if (living(state.enemies).length === 0) {
    if (state.waveIndex + 1 < state.waveCount) {
      spawnWave(state, state.waveIndex + 1);
      note(state, 'the next wave steps in');
    } else {
      finish(state, 'victory', 'cleared');
      return state;
    }
  }

  if (state.tick >= state.limitTicks) finish(state, 'defeat', 'timeout');
  return state;
}

export function isFinished(state: CombatState): boolean {
  return state.finished;
}

export function finalizeCombat(state: CombatState): CombatResult {
  const victory = state.outcome === 'victory';
  return {
    outcome: victory ? 'victory' : 'defeat',
    reason: state.reason ?? 'dead',
    ticks: state.tick,
    seconds: state.tick * TICK_SECONDS,
    kills: state.kills,
    gold: state.kills * state.goldPerKill + (victory ? state.clearGold : 0),
    xp: victory ? state.clearXp : 0,
    dust: victory ? state.clearDust : 0,
    dropRolls: victory ? state.dropRolls : 0,
    damageDealt: state.damageDealt,
    log: state.log.slice(),
    heroHp: Math.max(0, state.hero.hp),
    heroMaxHp: state.hero.maxHp,
    stageId: state.stageId,
  };
}

export function runCombat(input: {
  hero: ResolvedHero;
  stage: StageDef;
  seed: number;
  recordLog?: boolean;
  /** Walls scale up below their level floor. Set false only to measure the unscaled wall. */
  scaleWalls?: boolean;
}): CombatResult {
  let state = createCombat(input);
  let guard = 0;
  while (!state.finished && guard < 5000) {
    state = stepCombat(state);
    guard += 1;
  }
  if (!state.finished) finish(state, 'defeat', 'timeout');
  return finalizeCombat(state);
}
